import type { DeviceLocation } from "@/entities/DeviceLocation";

const API_BASE_URL = process.env.API_URL || "http://localhost:8000/api";

/**
 * Custom error class for API-related errors.
 * Used to differentiate standard runtime errors from API request failures or non-200 HTTP statuses.
 */
export class DeviceApiError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = "DeviceApiError";
  }
}

/**
 * Service for interacting with the backend API regarding device location and tracking.
 * Provides methods for standard REST fetching and real-time WebSocket communication.
 */
export const deviceService = {
  /**
   * Fetches the current list of devices and their locations from the REST API.
   *
   * @returns {Promise<DeviceLocation[]>} A promise resolving to an array of mapped DeviceLocation objects.
   * @throws {DeviceApiError} If the HTTP request fails or returns an error status.
   */
  async getDevices(): Promise<DeviceLocation[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/devices`);

      if (!response.ok) {
        throw new DeviceApiError(
          `Failed to fetch devices: ${response.statusText}`,
          response.status,
        );
      }

      const data: DeviceLocation[] = await response.json();
      return data;
    } catch (error) {
      if (error instanceof DeviceApiError) {
        throw error; // Re-throw API errors
      }
      throw new DeviceApiError(
        error instanceof Error
          ? error.message
          : "Unknown error occurred while fetching devices",
      );
    }
  },

  /**
   * Establishes a WebSocket connection for receiving real-time device updates.
   * The connection is configured to auto-reply to "ping" messages to keep the socket alive.
   *
   * @param onMessage - Callback function invoked whenever a valid list of devices is received.
   * @returns {WebSocket} The established WebSocket connection. The caller is responsible for calling `.close()` when done.
   */
  connectWebSocket(onMessage: (devices: DeviceLocation[]) => void): WebSocket {
    const ws = new WebSocket(
      `${API_BASE_URL.replace(/^http/, "ws")}/devices/ws`,
    );

    ws.onopen = () => {
      console.log("WebSocket connection established");
    };

    ws.onmessage = (event) => {
      try {
        // Check if it's a ping first
        if (event.data === "ping") {
          ws.send("pong");
          return;
        }

        console.log("Received WebSocket message:", event.data);
        const devices: DeviceLocation[] = JSON.parse(event.data);
        // console.log("Parsed devices from WebSocket message:", devices);
        onMessage(devices);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.onclose = (event) => {
      console.log("WebSocket connection closed:", event);
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    return ws; // Caller is responsible for closing the WebSocket when done
  },

  // Clear all devices
  async clearDevices(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/devices/clear`);

      if (!response.ok) {
        throw new DeviceApiError(
          `Failed to clear devices: ${response.statusText}`,
          response.status,
        );
      }
    } catch (error) {
      if (error instanceof DeviceApiError) {
        throw error; // Re-throw API errors
      }
      throw new DeviceApiError(
        error instanceof Error
          ? error.message
          : "Unknown error occurred while clearing devices",
      );
    }
  },
};
